import type { FragmentType } from 'src/gql';
import Link from 'next/link';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import numeral from 'numeral';
import { useOrganizationBranding } from 'src/components/dashboard/use-organization-branding';
import { ItemType } from 'src/constants/account-link';
import { PORTFOLIO_ITEM_GROUP_DISPLAY_NAMES } from 'src/constants/portfolio-item-group';
import { graphql, unmaskFragment } from 'src/gql';

import type { PortfolioItemGroupName } from '@savvy/constants/generated/gql-enums';
import type { ID } from '@savvy/utils/id';
import {
  PORTFOLIO_ITEM_GROUP_BANK_ACCOUNTS,
  PORTFOLIO_ITEM_GROUP_COMPANY_EQUITY,
  PORTFOLIO_ITEM_GROUP_OTHER_ASSETS,
  PORTFOLIO_ITEM_GROUP_OTHER_INVESTMENTS,
  PORTFOLIO_ITEM_GROUP_OTHER_LIABILITIES,
  PORTFOLIO_ITEM_GROUP_REAL_ESTATE,
  PORTFOLIO_ITEM_GROUP_SAVVY_ACCOUNTS,
} from '@savvy/constants/portfolio-item-groups';
import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';
import { hasKey } from '@savvy/utils/typescript';

import { useHouseholdWriteAccess } from '../../hooks/use-household-write-access';
import TrackedButton from '../tracked/tracked-button';
import { ManagedTagIcon } from './icons/managed-tag';

export const NetWorthAccountGroupTableTitleFragment = graphql(/* GraphQL */ `
  fragment NetWorthAccountGroupTableTitle on PortfolioItemGroup {
    hiddenFromClientTotalValue {
      amount
    }
    name
    totalValue {
      amount
    }
  }
`);

type Props = {
  readonly hiddenAccountsView: boolean;
  readonly hasItems: boolean;
  readonly portfolioItemGroupFrag: FragmentType<typeof NetWorthAccountGroupTableTitleFragment>;
  readonly advisorName: string | undefined;
  readonly advisorView: boolean | undefined;
  readonly clientUserId?: ID;
  readonly personalPortfolioId: ID | null;
  readonly onClickAddAccountButton: (itemType: ItemType | undefined) => void;
};

export function NetWorthAccountGroupTableTitle({
  hiddenAccountsView,
  hasItems,
  portfolioItemGroupFrag,
  advisorName,
  advisorView,
  clientUserId,
  personalPortfolioId,
  onClickAddAccountButton,
}: Props) {
  const portfolioItemGroup = unmaskFragment(NetWorthAccountGroupTableTitleFragment, portfolioItemGroupFrag);
  const totalGroupValue = hiddenAccountsView
    ? portfolioItemGroup.hiddenFromClientTotalValue
    : portfolioItemGroup.totalValue;

  const isSmallScreen = useIsSmallScreen();

  const { branding: advisorGroupBranding } = useOrganizationBranding({ clientUserId });
  const brandName = advisorGroupBranding?.shortDisplayName ?? 'Savvy';

  const getDisplayName = (groupName: string) => {
    if (groupName === PORTFOLIO_ITEM_GROUP_SAVVY_ACCOUNTS) {
      return `${brandName} investments`;
    }
    return hasKey(PORTFOLIO_ITEM_GROUP_DISPLAY_NAMES, groupName)
      ? PORTFOLIO_ITEM_GROUP_DISPLAY_NAMES[groupName]
      : groupName;
  };

  if (isSmallScreen) {
    return (
      <div className="flex items-center justify-between gap-4 px-1">
        <Text variant="subtitle-lg" className="normal-case">
          {getDisplayName(portfolioItemGroup.name)}
        </Text>
        <div className="pr-1">
          <Text
            variant="subtitle-sm"
            color={
              portfolioItemGroup.name === PORTFOLIO_ITEM_GROUP_OTHER_LIABILITIES &&
              parseFloat(totalGroupValue.amount) > 0
                ? 'destructive'
                : 'default'
            }
          >
            {portfolioItemGroup.name === PORTFOLIO_ITEM_GROUP_OTHER_LIABILITIES &&
              parseFloat(totalGroupValue.amount) > 0 &&
              '-'}
            {numeral(totalGroupValue.amount).format('$0,0.00')}
          </Text>
        </div>
      </div>
    );
  }

  return (
    // Padding carried from the MUI header cells (pl-0 pr-0.5, pt-5 pb-3), as is the
    // divider they got from MUI's default cell border — dropped in the hidden-accounts
    // view when the group is empty, exactly as the `border` override used to do.
    <div
      className={cn(
        'flex items-end justify-between gap-4 pb-3 pl-0 pr-0.5 pt-5',
        !(hiddenAccountsView && !hasItems) && 'border-b border-light',
      )}
    >
      <div className="flex flex-1 flex-row items-center gap-4">
        <Text variant="h4" className="normal-case">
          {getDisplayName(portfolioItemGroup.name)}
        </Text>
        <Text
          color={
            portfolioItemGroup.name === PORTFOLIO_ITEM_GROUP_OTHER_LIABILITIES && parseFloat(totalGroupValue.amount) > 0
              ? 'destructive'
              : 'secondary'
          }
        >
          {portfolioItemGroup.name === PORTFOLIO_ITEM_GROUP_OTHER_LIABILITIES &&
            parseFloat(totalGroupValue.amount) > 0 &&
            '-'}
          {numeral(totalGroupValue.amount).format('$0,0.00')}
        </Text>
        {portfolioItemGroup.name === PORTFOLIO_ITEM_GROUP_SAVVY_ACCOUNTS && (
          <>
            <ManagedTagIcon />
            {advisorName && (
              <Text color="secondary" className="!ml-1">
                {`Managed by ${advisorName}`}
              </Text>
            )}
          </>
        )}
      </div>
      <div className="flex items-end gap-2 pr-0.5">
        <AddAccountButton
          portfolioItemGroupName={portfolioItemGroup.name}
          onClickAddAccountButton={onClickAddAccountButton}
        />
        {portfolioItemGroup.name === PORTFOLIO_ITEM_GROUP_SAVVY_ACCOUNTS && !advisorView && (
          <TrackedButton
            elementName="net-worth-account-group-table-title-explore-portfolio-button"
            className="-mb-1"
            render={<Link href={`/savvy-portfolio/${personalPortfolioId}`} />}
          >
            Explore portfolio -&gt;
          </TrackedButton>
        )}
      </div>
    </div>
  );
}

type AddAccountButtonProps = {
  readonly portfolioItemGroupName: PortfolioItemGroupName;
  readonly onClickAddAccountButton: (itemType: ItemType | undefined) => void;
};
function AddAccountButton({ portfolioItemGroupName, onClickAddAccountButton }: AddAccountButtonProps) {
  const { canWrite } = useHouseholdWriteAccess();
  let addAccountText = 'Add account';
  switch (portfolioItemGroupName) {
    case 'BANK_ACCOUNTS':
      addAccountText = 'Add cash account';
      break;
    case 'SAVVY_ACCOUNTS':
      return null;
    case 'OTHER_INVESTMENTS':
      addAccountText = 'Add investment account';
      break;
    case 'OTHER_LIABILITIES':
      addAccountText = 'Add liability';
      break;
    case 'REAL_ESTATE':
      addAccountText = 'Add real estate';
      break;
    case 'OTHER_ASSETS':
      addAccountText = 'Add asset';
      break;
    case 'COMPANY_EQUITY':
      addAccountText = 'Add company equity';
      break;
  }
  if (!canWrite) {
    return null;
  }
  return (
    <TrackedButton
      elementName="net-worth-account-group-table-title-add-account-button"
      size="sm"
      variant="secondary"
      onClick={() => {
        onClickAddAccountButton(
          hasKey(PORTFOLIO_ITEM_GROUP_NAME_TO_ITEM_TYPE, portfolioItemGroupName)
            ? PORTFOLIO_ITEM_GROUP_NAME_TO_ITEM_TYPE[portfolioItemGroupName]
            : undefined,
        );
      }}
    >
      {addAccountText}
    </TrackedButton>
  );
}

const PORTFOLIO_ITEM_GROUP_NAME_TO_ITEM_TYPE = {
  [PORTFOLIO_ITEM_GROUP_BANK_ACCOUNTS]: ItemType.BANK_ACCOUNT,
  [PORTFOLIO_ITEM_GROUP_SAVVY_ACCOUNTS]: undefined,
  [PORTFOLIO_ITEM_GROUP_OTHER_INVESTMENTS]: ItemType.INVESTMENT_ACCOUNT,
  [PORTFOLIO_ITEM_GROUP_OTHER_LIABILITIES]: ItemType.OTHER_LIABILITY,
  [PORTFOLIO_ITEM_GROUP_COMPANY_EQUITY]: ItemType.COMPANY_EQUITY,
  [PORTFOLIO_ITEM_GROUP_REAL_ESTATE]: ItemType.REAL_ESTATE,
  [PORTFOLIO_ITEM_GROUP_OTHER_ASSETS]: ItemType.OTHER_ASSET,
};
